import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { propTypes } from '../../util/types';

// Import ShareTribe Layout Components
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

// Import custom sections
import SectionHeroBranded from './SectionHeroBranded';
import SectionStats from './SectionStats';
import SectionTrust from './SectionTrust';
import SectionHowItWorks from './SectionHowItWorks';
import SectionTestimonials from './SectionTestimonials';
import SectionFAQ from './SectionFAQ';
import SectionFinalCTA from './SectionFinalCTA';
import SectionGuarantee from './SectionGuarantee';
import SectionCityGrid from './SectionCityGrid';
import SectionComparison from './SectionComparison';

import css from './LandingPage.module.css';

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error, intl } = props;

  // Hero section props
  const heroProps = {
    title: 'Find a Personal Trainer Who Gets You Results.',
    subtitle: 'Book certified trainers for in-person or virtual sessions. Get a custom training and nutrition plan — and message your trainer directly, all in one place.',
    description: 'No membership fees · Pay-as-you-go · Personalized plans included',
    primaryButtonText: 'Find a Trainer →',
    primaryButtonLink: '/s',
    secondaryButtonText: 'See How It Works',
    secondaryButtonLink: '#how-it-works',
    trustBadges: [
      { text: 'Background-Checked Trainers' },
      { text: '5/5 Average Rating' },
      { text: 'Secure Payment' },
    ],
    note: 'No credit card required to browse trainers',
  };

  // Stats section props
  const statsProps = {
    stats: [
      { number: '80+', label: 'Fitness Professionals' },
      { number: '5.0★', label: 'Average Rating' },
      { number: '20+', label: 'Cities Available' },
      { number: '100%', label: 'ID-Verified' },
    ],
  };

  // Trust section props
  const trustProps = {
    title: 'Why Choose GymNet?',
    subtitle: 'More than booking — a complete fitness experience built around you',
    certifications: ['NASM', 'ACE', 'ISSA', 'NSCA'],
    cards: [
      {
        icon: 'verified',
        title: 'Background-Checked Professionals',
        description: 'Every trainer on GymNet completes a background check and earns a Verified Professional badge — visible right on their profile so you know exactly who you\'re working with.',
      },
      {
        icon: 'certified',
        title: 'NASM / ACE / ISSA Certified',
        description: 'Our professionals hold recognized certifications — NASM, ACE, ISSA, and more — and display their credentials on their profiles.',
      },
      {
        icon: 'pricing',
        title: 'Personalized Training Plans',
        description: 'Your trainer builds a custom workout plan tailored to your goals — not a one-size-fits-all PDF, but a structured week-by-week program you can log and track.',
      },
      {
        icon: 'secure',
        title: 'Nutrition Guidance Included',
        description: 'Many trainers on GymNet also provide personalized nutrition plans with daily calorie and macro targets — so your diet and training work together.',
      },
      {
        icon: 'guarantee',
        title: 'First Session Promise',
        description: 'Not happy with your first session? We will make it right with a full refund or help you find a better fit.',
      },
      {
        icon: 'cancel',
        title: 'Message Your Trainer Directly',
        description: 'Have a question between sessions? Send your trainer a direct message right from the app — no phone numbers, no email chains, just a simple chat.',
      },
      {
        icon: 'ai',
        title: 'AI-Powered Plan Building',
        description: 'Every GymNet trainer gets an AI assistant that drafts workout plans, nutrition plans, and class descriptions in seconds — so your trainer spends less time on paperwork and more time coaching you.',
        link: '/p/partner-info',
      },
    ],
  };

  // How It Works section props
  const howItWorksProps = {
    title: 'How GymNet Works',
    subtitle: 'From first session to long-term results — everything in one place',
    steps: [
      {
        number: 1,
        title: 'Find Your Trainer',
        description: 'Browse trainers by location, specialty, or training style. Check their verified credentials, reviews, and see their profile page before you commit.',
        detail: 'Book a session that fits your schedule and pay securely — all in one place.',
        bonus: 'No credit card required to browse',
      },
      {
        number: 2,
        title: 'Train and Get Your Plan',
        description: 'Meet for 1-on-1 training in-person or virtually. Your trainer will build a personalized training and nutrition plan designed around your specific goals.',
        detail: 'Access your workout plan in the app, log each session, and stay accountable between visits.',
        bonus: 'Message your trainer directly with questions anytime',
      },
      {
        number: 3,
        title: 'Track Progress and Rebook',
        description: 'Log your workouts, track your nutrition, and watch your progress build week over week.',
        detail: 'Leave a review, rebook your trainer, and keep the momentum going — your plan evolves as you do.',
        bonus: 'Build your fitness journey one session at a time',
      },
    ],
    ctaButtonText: 'Find a Trainer →',
    ctaButtonLink: '/s',
  };

  // Testimonials section props
  const testimonialsProps = {
    title: 'What Our Clients Say',
    subtitle: 'Real reviews from real people achieving real results',
    testimonials: [
      {
        stars: 5,
        trainerName: 'Jamal',
        trainerCity: 'St. Louis, MO',
        text: 'My husband and I worked out with Jamal at the fitness factory in downtown St. Louis. He was super patient and informative on form and gave us tips to prevent injury. Jamal\'s calm demeanor put us at ease. He\'s in great shape and I know we could learn a lot from him.',
        authorName: 'Natalie K.',
        trainerLink: '/l/660245e9-083b-497b-be01-146b3abaa1dc',
        trainerLinkText: 'See Jamal\'s profile →',
      },
      {
        stars: 5,
        trainerName: 'Mike',
        trainerCity: 'Chicago, IL',
        text: 'Mike is an attentive and fun trainer to work with. He is very thoughtful and intentional with the workouts he prepares. He tailored the session to meet my and my friend\'s specific abilities and focus areas. I would highly recommend him for any client whether you\'re a beginner or a seasoned athlete.',
        authorName: 'Liz C. & Tess G.',
        trainerLink: '/l/65d54fcd-e387-415a-add3-1f581bc9c4e6',
        trainerLinkText: 'See Mike\'s profile →',
      },
      {
        stars: 5,
        trainerName: 'Dan',
        trainerCity: 'Atlanta, GA',
        text: 'I am a College soccer player, and Dan really helped make sure that my workout was Soccer specific and focused on helping me prepare for season! He was really great to work with!',
        authorName: 'Natalia B.',
        trainerLink: '/u/66196e94-9b27-4c25-b46d-8e86dc19594e',
        trainerLinkText: 'See Dan\'s profile →',
      },
    ],
  };

  // FAQ section props
  const faqProps = {
    title: 'Frequently Asked Questions',
    subtitle: 'Everything you need to know about training with GymNet',
    faqs: [
      {
        question: 'How does payment work?',
        answer: 'Payment is secure and handled through our partner, Stripe. You pay when you book, and funds are held until after your session. We use bank-level encryption to protect your information.',
      },
      {
        question: 'Will my trainer create a personalized plan for me?',
        answer: 'Yes — trainers on GymNet can build custom week-by-week training plans tailored to your goals, fitness level, and schedule. Many trainers also offer nutrition plans with daily calorie and macro targets. You can access and log your plan directly in the app.',
      },
      {
        question: 'Can I message my trainer between sessions?',
        answer: 'Absolutely. GymNet includes built-in direct messaging so you can ask your trainer questions, share updates, or adjust your plan without exchanging phone numbers or emails.',
      },
      {
        question: 'Are fitness professionals verified?',
        answer: 'Yes. All trainers on GymNet complete a background check through our verification partner. Trainers who pass earn a Verified Professional badge that\'s displayed on their public profile — so you can see their status before you book.',
      },
      {
        question: 'What\'s included in a booking?',
        answer: 'Personal training includes 1-on-1 guidance and form correction for 30–60 minutes. Many trainers also include a custom training or nutrition plan as part of your ongoing program.',
      },
      {
        question: 'What\'s your cancellation policy?',
        answer: 'You can cancel up to 24 hours before your scheduled session for a full refund. Cancellations within 24 hours may not be eligible for refunds, but we work with you on a case-by-case basis for emergencies.',
      },
      {
        question: 'Do I need my own equipment?',
        answer: 'For in-person training, equipment is typically provided at the facility. For virtual training, trainers can design workouts with minimal or no equipment.',
      },
      {
        question: 'What if I\'m not satisfied?',
        answer: 'We offer a satisfaction guarantee on your first session. If you\'re not happy, contact us within 24 hours and we\'ll either provide a full refund or match you with a different trainer at no extra cost.',
      },
    ],
  };

  // Final CTA section props
  const finalCTAProps = {
    title: 'Ready to Start Your Fitness Journey?',
    subtitle: 'Find a background-checked trainer, get a personalized plan, and start seeing results — all in one place.',
    buttonText: 'Find a Trainer →',
    buttonLink: '/s',
    badges: [
      { text: 'Verified Professionals' },
      { text: 'Custom Training Plans' },
      { text: 'Satisfaction Guaranteed' },
    ],
  };

  // SEO Meta tags
  const schemaTitle = 'Find a Personal Trainer — Custom Plans, Direct Messaging & More | GymNet';
  const schemaDescription = 'Book certified, background-checked personal trainers for in-person or virtual sessions. Get a personalized training and nutrition plan, message your trainer directly, and track your progress — all in one place.';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqProps.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={false}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        {/* Topbar */}
        <TopbarContainer />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Service',
              name: 'GymNet Personal Training Marketplace',
              description: schemaDescription,
              serviceType: 'Personal Training',
              provider: {
                '@type': 'Organization',
                name: 'GymNet',
                url: 'https://www.gymnet-fitness.com',
              },
              areaServed: [
                'Chicago, IL', 'Houston, TX', 'Dallas, TX', 'Atlanta, GA',
                'Phoenix, AZ', 'Los Angeles, CA', 'New York, NY', 'Miami, FL',
                'Seattle, WA', 'Denver, CO',
              ].map(name => ({ '@type': 'City', name })),
              url: 'https://www.gymnet-fitness.com',
            }),
          }}
        />

        {/* Main Content */}
        <div className={css.sections}>
          {/* Hero Section */}
          <SectionHeroBranded {...heroProps} />

          {/* Stats Section */}
          <SectionStats {...statsProps} />

          {/* No-Membership Comparison */}
          <SectionComparison />

          {/* Trust Indicators Section */}
          <SectionTrust {...trustProps} />

          {/* How It Works Section */}
          <div id="how-it-works">
            <SectionHowItWorks {...howItWorksProps} />
          </div>

          {/* Satisfaction Guarantee */}
          <SectionGuarantee />

          {/* Browse by City */}
          <SectionCityGrid />

          {/* Testimonials Section */}
          <SectionTestimonials {...testimonialsProps} />

          {/* FAQ Section */}
          <SectionFAQ {...faqProps} />

          {/* Final CTA Section */}
          <SectionFinalCTA {...finalCTAProps} />
        </div>

        {/* Footer */}
        <FooterContainer />
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.defaultProps = {
  pageAssetsData: {},
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;